import { ColumnSortParams } from 'primereact/column'
import { ApprenticeModuleGrade } from '../../../model/apprenticeModuleGrade.model'
import { DossierDb, MokaiDocumentType } from '../../../model/dossier.model'
import { Apprentice, Person } from '../../../model/person.model'
import { Profession } from '../../../model/profession.model'
import { TrialPeriodReport } from '../../../model/trialPeriodReport.model'
import { findApprenticeById, findPersonById } from '../../../util/arraySearch'
import { getPersonFullName } from '../../../util/getPersonName'
import { getUserAccountTitleString } from '../../../util/getUserAccountTitleString'

import { ApprenticeDossier } from '../../content/document/education-report/list/EducationReportList'

/** Given an array of persons, sort them by their titles alphabetical order */
export const sortByAccountTitle = (e: ColumnSortParams) => {
  e.data.sort((a: Person, b: Person) => {
    // ascending
    if (e.order === 1) {
      return getUserAccountTitleString(a.user_account_type, a.salutation) >
        getUserAccountTitleString(b.user_account_type, b.salutation)
        ? 1
        : -1
    }
    // descending
    else {
      return getUserAccountTitleString(a.user_account_type, a.salutation) <
        getUserAccountTitleString(b.user_account_type, b.salutation)
        ? 1
        : -1
    }
  })
  return e.data
}

/** Given an array of apprentices, sort them by their profession */
export const sortProfession = (
  e: ColumnSortParams,
  professions: Profession[]
) => {
  e.data.sort((a: Apprentice, b: Apprentice) => {
    const profID_a = a.apprentice?.id_profession
    const profID_b = b.apprentice?.id_profession
    const professionString_a =
      professions.find((p) => p.profession_id === profID_a)?.profession || ''
    const professionString_b =
      professions.find((p) => p.profession_id === profID_b)?.profession || ''

    if (e.order === 1) {
      return professionString_a > professionString_b ? 1 : -1
    }
    // descending
    else {
      return professionString_a < professionString_b ? 1 : -1
    }
  })

  return e.data
}

/** Given an array of ApprenticeDossiers, sort them by their profession
 * Can be extended by adding types to the sort function for all types that have profession_id on the first level
 * Needs to exist separately for sake of different naming in ApprenticeDossier and Dossier
 */
export const sortProfessionFromEdrDossier = (
  e: ColumnSortParams,
  professions: Profession[]
) => {
  e.data.sort((a: ApprenticeDossier, b: ApprenticeDossier) => {
    const profID_a = a.profession_id
    const profID_b = b.profession_id
    const professionString_a =
      professions.find((p) => p.profession_id === profID_a)?.profession || ''
    const professionString_b =
      professions.find((p) => p.profession_id === profID_b)?.profession || ''

    if (e.order === 1) {
      return professionString_a > professionString_b ? 1 : -1
    }
    // descending
    else {
      return professionString_a < professionString_b ? 1 : -1
    }
  })

  return e.data
}

export const sortPersonsByName = (e: ColumnSortParams, persons: Person[]) => {
  e.data.sort((a: Person, b: Person) => {
    const person_a = findPersonById(a.person_id, persons)
    const person_b = findPersonById(b.person_id, persons)

    // should never default to empty string but in case it does
    // we simply handle the case
    const personName_a = getPersonFullName(person_a) || ''
    const personName_b = getPersonFullName(person_b) || ''

    if (e.order === 1) {
      return personName_a > personName_b ? 1 : -1
    }
    // descending
    else {
      return personName_a < personName_b ? 1 : -1
    }
  })
  return e.data
}

/** Given an array with a type with id_apprentice on top level, returns them sorted by name */
export const sortByApprenticeNameFromInnerApprenticeId = (
  e: ColumnSortParams,
  apprentices: Apprentice[]
) => {
  e.data.sort(
    (
      a: ApprenticeModuleGrade | TrialPeriodReport,
      b: ApprenticeModuleGrade | TrialPeriodReport
    ) => {
      const apprentice_a = findApprenticeById(a.id_apprentice, apprentices)
      const apprentice_b = findApprenticeById(b.id_apprentice, apprentices)

      // should never default to empty string but in case it does
      // we simply handle the case
      const apprenticeName_a = getPersonFullName(apprentice_a) || ''
      const apprenticeName_b = getPersonFullName(apprentice_b) || ''

      if (e.order === 1) {
        return apprenticeName_a > apprenticeName_b ? 1 : -1
      }
      // descending
      else {
        return apprenticeName_a < apprenticeName_b ? 1 : -1
      }
    }
  )
  return e.data
}

/**  Given  an array of types with id_persion on top level, returns them sorted by name
 * Can be extended to accept other types
 */
export const sortByPersonNameFromInnerPersonId = (
  e: ColumnSortParams,
  persons: Person[]
) => {
  e.data.sort((a: DossierDb, b: DossierDb) => {
    const person_a = findPersonById(a.id_person, persons)
    const person_b = findPersonById(b.id_person, persons)

    // should never default to empty string but in case it does
    // we simply handle the case
    const personName_a = getPersonFullName(person_a) || ''
    const personName_b = getPersonFullName(person_b) || ''

    if (e.order === 1) {
      return personName_a > personName_b ? 1 : -1
    }
    // descending
    else {
      return personName_a < personName_b ? 1 : -1
    }
  })
  return e.data
}

/** Given a type with apprentice on the top level, sort them by name
 * Can be extended for other types
 */
export const sortApprenticesByEdrDossier = (e: ColumnSortParams) => {
  e.data.sort((a: ApprenticeDossier, b: ApprenticeDossier) => {
    // should never default to empty string but in case it does
    // we simply handle the case
    const apprenticeName_a = getPersonFullName(a.apprentice) || ''
    const apprenticeName_b = getPersonFullName(b.apprentice) || ''

    if (e.order === 1) {
      return apprenticeName_a > apprenticeName_b ? 1 : -1
    }
    // descending
    else {
      return apprenticeName_a < apprenticeName_b ? 1 : -1
    }
  })

  return e.data
}

/** Given an array of type with documentID on type level and the currently supported document types
 * Sorts it by document type
 * Can be extended to support other types
 */
export const sortDocumentTypes = (
  e: ColumnSortParams,
  documentTypes: MokaiDocumentType[]
) => {
  e.data.sort((a: DossierDb, b: DossierDb) => {
    const dTName_a =
      documentTypes.find((dT) => dT.document_type_id === a.id_documentType)
        ?.document_type_name || ''
    const dTName_b =
      documentTypes.find((dT) => dT.document_type_id === b.id_documentType)
        ?.document_type_name || ''

    if (e.order === 1) {
      return dTName_a > dTName_b ? 1 : -1
    }
    // descending
    else {
      return dTName_a < dTName_b ? 1 : -1
    }
  })
  return e.data
}